/**
 * 인증 코드 6칸 입력 (template-parts/code-input.php 와 짝).
 *
 * 코드 확인 화면 넷(/demo/ · /setup/ · /register/ · /my/profile/)이 같은 칸 UI 를 쓴다.
 * 붙여넣기 · 자동완성으로 여러 자리가 한 번에 들어오는 것도 여기서 나눠 담는다.
 * 화면 JS 는 컨트롤러(value · clear · focus)만 쓰고, input · keydown 이벤트는 감싸는 요소(container)로
 * 올라오므로 거기서 듣는다 (예: 엔터로 확인, 값이 바뀌면 이전 안내 지우기).
 */

// container 하나당 컨트롤러 하나 — 같은 요소로 다시 부르면 새로 잇지 않고 그대로 돌려준다
const controllers = new WeakMap();

/**
 * @param {Element | null} container [data-code-input] 를 가진 감싸는 요소
 * @returns {{ value: () => string, clear: () => void, focus: () => void } | null}
 */
const initCodeInput = (container) => {
  if (!container) {
    return null;
  }

  if (controllers.has(container)) {
    return controllers.get(container);
  }

  const cells = [...container.querySelectorAll('[data-code-input-cell]')];

  if (0 === cells.length) {
    return null;
  }

  /** index 칸부터 숫자를 차례로 채우고, 이어서 입력할 칸으로 포커스를 옮긴다. */
  const fillFrom = (index, digits) => {
    digits.split('').forEach((digit, offset) => {
      const cell = cells[index + offset];

      if (cell) {
        cell.value = digit;
      }
    });

    // 한 자리면 다음 칸으로, 여러 자리면 마지막으로 채운 칸에 머문다
    const next = Math.min(index + digits.length, cells.length - 1);

    cells[next].focus();
  };

  // 한 칸에 숫자 하나. 입력하면 다음 칸으로, 지우면 이전 칸으로 옮겨 간다.
  cells.forEach((cell, index) => {
    // 메일에서 코드를 통째로 복사해 붙이는 경우.
    // maxlength=1 이라 브라우저가 값을 한 글자로 잘라 버리므로 여기서 직접 나눠 담는다.
    cell.addEventListener('paste', (event) => {
      const pasted = (event.clipboardData || window.clipboardData)?.getData('text') || '';
      const digits = pasted.replace(/\D/g, '').slice(0, cells.length - index);

      if ('' === digits) {
        return;
      }

      event.preventDefault();
      fillFrom(index, digits);
    });

    cell.addEventListener('input', () => {
      const digits = cell.value.replace(/\D/g, '');

      cell.value = '';

      if ('' === digits) {
        return;
      }

      // 자동완성(문자·메일 인증코드)처럼 여러 자리가 한 번에 들어오는 경우도 나눠 담는다
      fillFrom(index, digits.slice(0, cells.length - index));
    });

    cell.addEventListener('keydown', (event) => {
      if ('Backspace' === event.key && '' === cell.value && index > 0) {
        event.preventDefault();
        cells[index - 1].value = '';
        cells[index - 1].focus();
        return;
      }

      if ('ArrowLeft' === event.key && index > 0) {
        event.preventDefault();
        cells[index - 1].focus();
        return;
      }

      if ('ArrowRight' === event.key && index < cells.length - 1) {
        event.preventDefault();
        cells[index + 1].focus();
      }
    });

    cell.addEventListener('focus', () => {
      cell.select();
    });
  });

  const controller = {
    value: () => cells.map((cell) => cell.value).join(''),
    clear: () => {
      cells.forEach((cell) => {
        cell.value = '';
      });
    },
    focus: () => {
      cells[0].focus();
    },
  };

  controllers.set(container, controller);

  return controller;
};

/** 화면에 있는 [data-code-input] 를 전부 잇는다 (/ui/ 견본 포함) */
export const InitCodeInputs = () => {
  document.querySelectorAll('[data-code-input]').forEach((container) => initCodeInput(container));
};

export default initCodeInput;
